import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import type {ApexOptions} from 'apexcharts'
import {VistaPills} from '@/components/bateria/vista-pills'
import {ApexChart, donutCenter, useChartTokens, useSegmentNavigation} from '@/components/ui/apex-chart'
import {NIVELES} from '@/lib/bateria'
import {
  apiladoPorNivel,
  coloresDeNivel,
  comparativaPromedio,
  comparativaSegmentos,
  porcentaje,
  type EntradaNivel,
  type EntradaSegmento,
} from '@/lib/bateria-charts'

// La leyenda de Apex cumple la regla de la casa (lleva rótulo escrito) y deja apagar niveles. El
// tooltip no se comparte: con casi todos los niveles en cero listaría cinco filas para decir una.

function PuntoLeyenda({color}: {color: string}) {
  return <span aria-hidden className="inline-block size-2.5 shrink-0 rounded-[2px]" style={{backgroundColor: color}} />
}

/** Para gráficas que colorean por nivel sin una serie por nivel, donde Apex listaría las entidades. */
function LeyendaRampa({colores}: {colores: Record<string, string>}) {
  const {t} = useTranslation()
  return (
    <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5" data-testid="leyenda-rampa">
      {NIVELES.map((n) => (
        <li key={n} className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <PuntoLeyenda color={colores[n]} />
          {t(`admin.bateria.nivel.${n}`)}
        </li>
      ))}
    </ul>
  )
}

function useColoresDeNivel(): Record<string, string> {
  const tokens = useChartTokens()
  return useMemo(() => coloresDeNivel(tokens), [tokens])
}

// Identidad estable: una flecha en línea cambiaría las opciones en cada render y Apex no animaría el
// cambio de datos.
const SIN_NAVEGACION = () => {}

/** Recibe la `distribucion` cruda: `NivelDistribucion` y `NivelResumen` son dos serializaciones del
 * mismo agregado. */
export function NivelDonut({
  distribucion,
  label,
  size,
  testId,
  chartTestId = 'total-general-donut-chart',
}: {
  distribucion: Record<string, number>
  label: string
  size?: string
  testId?: string
  chartTestId?: string
}) {
  const {t} = useTranslation()
  const tokens = useChartTokens()
  const colores = useColoresDeNivel()
  const presentes = useMemo(() => NIVELES.filter((n) => (distribucion[n] ?? 0) > 0), [distribucion])
  const total = presentes.reduce((acc, n) => acc + (distribucion[n] ?? 0), 0)

  const options = useMemo<ApexOptions>(
    () => ({
      labels: presentes.map((n) => t(`admin.bateria.nivel.${n}`)),
      colors: presentes.map((n) => colores[n]),
      stroke: {width: 2, colors: ['transparent']},
      plotOptions: donutCenter({tokens, label: t('admin.bateria.resultados.donutTotalLabel'), total, size}),
      tooltip: {
        y: {
          formatter: (value: number) =>
            t('admin.bateria.resultados.conteoPct', {count: value, pct: porcentaje(value, total)}),
        },
      },
    }),
    [presentes, colores, tokens, t, total, size]
  )

  if (total === 0) {
    return <p className="py-8 text-center text-xs text-muted-foreground">{t('admin.bateria.resultados.sinValidos')}</p>
  }

  return (
    <div data-testid={testId}>
      <ApexChart
        type="donut"
        height={190}
        options={options}
        series={presentes.map((n) => distribucion[n] ?? 0)}
        testId={chartTestId}
      />
      <table className="sr-only">
        <caption>{label}</caption>
        <tbody>
          {NIVELES.map((n) => (
            <tr key={n}>
              <th scope="row">{t(`admin.bateria.nivel.${n}`)}</th>
              <td>{distribucion[n] ?? 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** Barras apiladas al 100%, peor primero; `onPick` lleva el clic de un segmento a su fila de la tabla. */
export function NivelApilado({
  entradas,
  ariaLabel,
  testId,
  onPick,
}: {
  entradas: EntradaNivel[]
  ariaLabel: string
  testId: string
  onPick?: (clave: string) => void
}) {
  const {t} = useTranslation()
  const tokens = useChartTokens()
  const colores = useColoresDeNivel()
  const apilado = useMemo(() => apiladoPorNivel(entradas), [entradas])
  const seleccionar = useSegmentNavigation(apilado.claves, onPick ?? SIN_NAVEGACION)

  const options = useMemo<ApexOptions>(
    () => ({
      chart: {
        type: 'bar',
        stacked: true,
        stackType: '100%',
        ...(onPick ? {events: {dataPointSelection: seleccionar}} : {}),
      },
      colors: NIVELES.map((n) => colores[n]),
      plotOptions: {bar: {horizontal: true, borderRadius: 2, barHeight: '64%'}},
      xaxis: {
        categories: apilado.categorias,
        labels: {formatter: (value: string) => `${value}%`},
      },
      yaxis: {labels: {maxWidth: 260}},
      legend: {
        show: true,
        position: 'bottom',
        horizontalAlign: 'left',
        fontSize: '11px',
        fontWeight: 500,
        labels: {colors: tokens['muted-foreground']},
        markers: {size: 5, offsetX: -2},
        itemMargin: {horizontal: 10, vertical: 2},
      },
      tooltip: {
        shared: false,
        intersect: true,
        y: {
          formatter: (value: number, opts?: {dataPointIndex: number}) =>
            t('admin.bateria.resultados.conteoPct', {
              count: value,
              pct: porcentaje(value, apilado.totales[opts?.dataPointIndex ?? 0] ?? 0),
            }),
        },
      },
    }),
    [apilado, colores, tokens, t, onPick, seleccionar]
  )
  const series = useMemo(
    () => apilado.series.map((s) => ({name: t(`admin.bateria.nivel.${s.nivel}`), data: s.data})),
    [apilado, t]
  )

  if (apilado.claves.length === 0) return null

  return (
    <div role="img" aria-label={ariaLabel}>
      <ApexChart
        type="bar"
        height={Math.max(190, apilado.claves.length * 34 + 60)}
        options={options}
        series={series}
        clickable={Boolean(onPick)}
        testId={testId}
      />
    </div>
  )
}

/** Cada barra lleva su propio color (`distributed`), así que la leyenda de niveles va aparte como texto. */
export function NivelPromedio({
  entradas,
  ariaLabel,
  testId,
}: {
  entradas: EntradaNivel[]
  ariaLabel: string
  testId: string
}) {
  const {t} = useTranslation()
  const tokens = useChartTokens()
  const colores = useColoresDeNivel()
  const comparativa = useMemo(() => comparativaPromedio(entradas), [entradas])

  const options = useMemo<ApexOptions>(
    () => ({
      chart: {type: 'bar'},
      colors: comparativa.niveles.map((n) => colores[n ?? ''] ?? tokens['muted-foreground']),
      plotOptions: {
        bar: {
          horizontal: true,
          distributed: true,
          borderRadius: 3,
          borderRadiusApplication: 'end',
          barHeight: '58%',
        },
      },
      xaxis: {categories: comparativa.categorias, max: 100},
      yaxis: {labels: {maxWidth: 260}},
      legend: {show: false},
      tooltip: {
        y: {
          formatter: (value: number, opts?: {dataPointIndex: number}) => {
            const i = opts?.dataPointIndex ?? 0
            const nivel = comparativa.niveles[i]
            return t('admin.bateria.resultados.promedioTooltip', {
              count: comparativa.personas[i] ?? 0,
              nivel: nivel ? t(`admin.bateria.nivel.${nivel}`) : t('admin.bateria.nivel.sinDato'),
              value,
            })
          },
        },
      },
    }),
    [comparativa, colores, tokens, t]
  )
  const series = useMemo(
    () => [{name: t('admin.bateria.resultados.comparacionPromedio'), data: comparativa.data}],
    [comparativa, t]
  )

  if (comparativa.claves.length === 0) return null

  return (
    <div role="img" aria-label={ariaLabel}>
      <ApexChart
        type="bar"
        height={Math.max(170, comparativa.claves.length * 36)}
        options={options}
        series={series}
        testId={testId}
      />
      <LeyendaRampa colores={colores} />
    </div>
  )
}

/** Una barra por segmento con su % en riesgo; la barra o el chip del segmento abren su detalle. */
export function SegmentosEnRiesgo({
  segmentos,
  ariaLabel,
  seleccionado,
  onPick,
}: {
  segmentos: EntradaSegmento[]
  ariaLabel: string
  seleccionado: string | null
  onPick: (clave: string) => void
}) {
  const {t} = useTranslation()
  const tokens = useChartTokens()
  const colores = useColoresDeNivel()
  const comparativa = useMemo(() => comparativaSegmentos(segmentos), [segmentos])
  const seleccionar = useSegmentNavigation(comparativa.claves, onPick)
  const opciones = useMemo(
    () => comparativa.claves.map((clave, i) => ({key: clave, label: comparativa.categorias[i]})),
    [comparativa]
  )

  const options = useMemo<ApexOptions>(
    () => ({
      chart: {type: 'bar', events: {dataPointSelection: seleccionar}},
      colors: comparativa.niveles.map((n) => colores[n ?? ''] ?? tokens['muted-foreground']),
      plotOptions: {
        bar: {
          horizontal: true,
          distributed: true,
          borderRadius: 3,
          borderRadiusApplication: 'end',
          barHeight: '56%',
        },
      },
      xaxis: {
        categories: comparativa.categorias,
        max: 100,
        labels: {formatter: (value: string) => `${value}%`},
      },
      yaxis: {labels: {maxWidth: 220}},
      legend: {show: false},
      tooltip: {
        y: {
          formatter: (value: number, opts?: {dataPointIndex: number}) => {
            const i = opts?.dataPointIndex ?? 0
            return t('admin.bateria.resultados.segmentoTooltipCorto', {
              count: comparativa.personas[i] ?? 0,
              enRiesgo: comparativa.enRiesgo[i] ?? 0,
              pct: value,
            })
          },
        },
      },
    }),
    [comparativa, colores, tokens, t, seleccionar]
  )
  const series = useMemo(
    () => [{name: t('admin.bateria.resultados.segmentoColRiesgo'), data: comparativa.data}],
    [comparativa, t]
  )

  if (comparativa.claves.length === 0) return null

  return (
    <div data-testid="segmento-comparacion">
      <div role="img" aria-label={ariaLabel}>
        <ApexChart
          type="bar"
          height={Math.max(170, comparativa.claves.length * 34)}
          options={options}
          series={series}
          clickable
          testId="segmentos-riesgo-chart"
        />
      </div>
      {/* Una barra en 0 % no tiene ancho que clicar, y Apex no lleva el Enter a `dataPointSelection`. */}
      <div className="mt-3">
        <VistaPills
          label={t('admin.bateria.resultados.segmentoDetalleLabel')}
          testIdPrefix="segmento-abrir"
          valor={seleccionado}
          onPick={onPick}
          opciones={opciones}
        />
      </div>
      <table className="sr-only">
        <caption>{ariaLabel}</caption>
        <thead>
          <tr>
            <th scope="col">{t('admin.bateria.resultados.comparacionEntidad')}</th>
            <th scope="col">{t('admin.bateria.resultados.segmentoColN')}</th>
            <th scope="col">{t('admin.bateria.resultados.segmentoColRiesgo')}</th>
          </tr>
        </thead>
        <tbody>
          {comparativa.claves.map((clave, i) => (
            <tr key={clave}>
              <th scope="row">{comparativa.categorias[i]}</th>
              <td>{comparativa.personas[i]}</td>
              <td>{comparativa.data[i]}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
